import { Injectable } from '@angular/core';
import { AppState } from '@api-ui-app/src/app/app.state';
import { getAssetsCollection } from '@api-ui-app/src/app/ngrx/assets.selector';
import { getSelectedClientId } from '@api-ui-app/src/app/ngrx/auth.reducer';
import { getCompanyDetailsCountryCode } from '@api-ui-app/src/app/ngrx/company-details.reducer';
import {
  loadPriceBreakdown,
  resetPriceBreakdown,
} from '@api-ui-app/src/app/shared/price-breakdown/+state/price-breakdown.actions';
import { PriceBreakdownService } from '@api-ui-app/src/app/shared/price-breakdown/price-breakdown.service';
import { PriceBreakdown, PriceBreakdownItem } from '@api-ui-app/src/app/shared/price-breakdown/types/price-breakdown';
import { UserCurrencyService } from '@api-ui-app/src/app/shared/services/user-currency.service';
import { SanityProductVariationTitleService } from '@gelato-api-ui/core/sanity/services/sanity-product-variation-title.service';
import { Store } from '@ngrx/store';
import {
  getIsEmbroiderySelected,
  getProductExtraPrintAreas,
  getSelectedCountry,
  getSelectedProductFromSanityContent,
  isApparelOrAccessoriesProductCategory,
} from '@product-catalogue/src/lib/ngrx/product-catalogue.reducer';
import { getSelectedPageCount } from '@product-catalogue/src/lib/ngrx/product-prices.reducer';
import { Dimensions } from '@product-catalogue/src/lib/product-catalogue/lib/dimentions';
import { Observable, combineLatest } from 'rxjs';
import { filter, first, map, tap, withLatestFrom } from 'rxjs/operators';
import {
  getDesignPriceBreakdown,
  getIncludedPrintArea,
  getIsLoading,
  getIsPriceBreakdownError,
  getPriceBreakdown,
  getProductBasePrice,
  getTotalDesignPrice,
  getTotalPrice,
  getTotalProductPrice,
} from './+state/price-breakdown.selector';

@Injectable({ providedIn: 'root' })
export class PriceBreakdownFacade {
  isApparelOrAccessoriesCategory$ = this.store.select(isApparelOrAccessoriesProductCategory);
  extraPrintAreas$ = this.store.select(getProductExtraPrintAreas);
  product$ = this.store.select(getSelectedProductFromSanityContent);
  clientId$ = this.store.select(getSelectedClientId);
  pageCount$ = this.store.select(getSelectedPageCount);
  isEmbroiderySelected$ = this.store.select(getIsEmbroiderySelected);
  totalPrice$ = this.store.select(getTotalPrice).pipe(filter(price => Boolean(price)));
  totalProductPrice$ = this.store.select(getTotalProductPrice);
  includedPrintArea$ = this.store.select(getIncludedPrintArea);
  totalDesignPrice$ = this.store.select(getTotalDesignPrice);
  productBasePrice$ = this.store.select(getProductBasePrice);
  assetsCollection$ = this.store.select(getAssetsCollection);
  isPriceBreakdownError$ = this.store.select(getIsPriceBreakdownError);
  catalogueCountry$ = this.store.select(getSelectedCountry);
  defaultCountry$ = this.store.select(getCompanyDetailsCountryCode);
  country$ = combineLatest([this.catalogueCountry$, this.defaultCountry$]).pipe(
    map(([catalogueCountry, defaultCountry]) => catalogueCountry || defaultCountry),
  );
  isPricesLoading$ = combineLatest([this.store.select(getIsLoading), this.totalPrice$, this.product$]).pipe(
    map(([isLoading, totalPrice, product]) => isLoading || !totalPrice || !product),
  );
  priceBreakdown$ = this.store.select(getPriceBreakdown).pipe(
    filter((data: PriceBreakdown) => Boolean(data)),
    map((data: PriceBreakdown): PriceBreakdownItem[] => this.service.formatForBreakdownList(data)),
  );
  priceBreakdownWithProduct$ = this.store.select(getPriceBreakdown).pipe(
    filter((data: PriceBreakdown) => Boolean(data)),
    map((data: PriceBreakdown): PriceBreakdownItem[] => this.service.format(data)),
  );
  designPriceBreakdown$ = this.store.select(getDesignPriceBreakdown).pipe(
    withLatestFrom(this.assetsCollection$),
    map(([designPriceItems, assetsCollection]) => {
      return !designPriceItems
        ? []
        : designPriceItems.map(item => ({
            ...item,
            assetProviderUid: this.service.getAssetProviderId(assetsCollection, item.imageId),
            imageUrl: this.service.getAssetPreview(assetsCollection, item.imageId),
          }));
    }),
  );
  currency$ = this.userCurrencyService.get();

  constructor(
    private readonly store: Store<AppState>,
    private readonly userCurrencyService: UserCurrencyService,
    private readonly service: PriceBreakdownService,
    private readonly sanityProductVariationTitleService: SanityProductVariationTitleService,
  ) {}

  loadPriceBreakdown(
    productUidForPrice: string,
    productTypeUid: string,
    designIdForPrice?: string,
    printSides: string[] = [],
    dimensions?: Dimensions,
    countryCode?: string,
  ) {
    combineLatest([
      this.country$,
      this.currency$,
      this.pageCount$,
      this.clientId$,
      this.isApparelOrAccessoriesCategory$,
      this.extraPrintAreas$,
    ])
      .pipe(
        map(value => {
          if (countryCode) {
            value[0] = countryCode;
          }
          return value;
        }),
        filter(([country, currency]) => Boolean(productUidForPrice) && Boolean(country) && Boolean(currency)),
        first(),
        tap(([country, currency, pageCount, clientId, isApparelOrAccessoriesCategory]) => {
          this.store.dispatch(
            loadPriceBreakdown({
              productUid: productUidForPrice,
              designId: designIdForPrice,
              printAreas: isApparelOrAccessoriesCategory ? printSides : [],
              trigger: new Date().getTime(),
              productTypeUid,
              country,
              currency,
              pageCount,
              clientId,
              dimensions,
            }),
          );
        }),
      )
      .subscribe();
  }

  getVariantTitle$(productUid: string): Observable<string> {
    return this.product$.pipe(
      map(product => this.sanityProductVariationTitleService.get(product, productUid, null, null, ', ')),
    );
  }

  resetPriceBreakdown() {
    this.store.dispatch(resetPriceBreakdown());
  }
}
